import { useApolloClient } from '@apollo/client';
import { Row, Typography, Upload, type UploadProps, message } from 'antd';
import { isNil } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    ImportInventoriesDocument,
    type ImportInventoriesMutation,
    type ImportInventoriesMutationVariables,
} from '../../../api/mutations/importInventories';
import { ListInventoriesDocument } from '../../../api/queries/listInventories';
import { CompanyTheme } from '../../../api/types';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import { ErrorContent } from '../../../components/importExport';
import CancelButton from '../../../components/modal/CancelButton';
import OkButton from '../../../components/modal/OkButton';
import { useThemeComponents } from '../../../themes';
import { allowedExtensions, validateExtensions } from '../../../utilities/extensions';
import getApolloErrors from '../../../utilities/getApolloErrors';

const StyledUpload = styled(Upload)`
    margin-left: 10px;
`;

export type ImportInventoryModalProps = {
    onClose: () => void;
    open: boolean;
};

const ImportInventoryModal = ({ open, onClose }: ImportInventoryModalProps) => {
    const { t } = useTranslation(['inventoryList', 'importExportExcel']);
    const { dealerIds } = useMultipleDealerIds();
    const [dealerId] = dealerIds;
    const apolloClient = useApolloClient();
    const [errors, setErrors] = useState<string>(null);
    const [updatedCount, setUpdatedCount] = useState<number>(null);
    const { theme, Modal } = useThemeComponents();

    const accept = useMemo(() => allowedExtensions.excel.join(','), []);

    const handleOnClose = useCallback(() => {
        onClose();
        setErrors(null);
        setUpdatedCount(null);
    }, [onClose]);

    const handleOnChange = useCallback(
        async ({ file }) => {
            if (file instanceof File) {
                try {
                    const { data } = await apolloClient.mutate<
                        ImportInventoriesMutation,
                        ImportInventoriesMutationVariables
                    >({
                        mutation: ImportInventoriesDocument,
                        variables: {
                            dealerId,
                            upload: file,
                        },
                        refetchQueries: [ListInventoriesDocument],
                    });

                    switch (data.result.__typename) {
                        case 'ImportInventorySuccess':
                            setUpdatedCount(data.result.updatedCount);
                            break;

                        case 'ImportInventoryFail':
                            setErrors(data.result.message);
                            break;

                        default:
                            throw new Error('Invalid response type');
                    }
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);
                    if (apolloErrors !== null) {
                        const { $root: rootError } = apolloErrors;

                        if (rootError) {
                            message.error(rootError);
                        }
                    } else {
                        console.error(error);
                    }
                }
            }
        },
        [apolloClient, dealerId]
    );

    const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
        file => {
            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(false);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [t]
    );

    const content = useMemo(() => {
        if (!isNil(errors)) {
            return <ErrorContent errors={errors} />;
        }

        if (!isNil(updatedCount)) {
            return (
                <Row align="middle" gutter={[14, 14]} justify="center">
                    <Typography>
                        {t('inventoryList:messages.importInventorySuccess', { count: updatedCount })}
                    </Typography>
                </Row>
            );
        }

        return (
            <Row align="middle" gutter={[14, 14]} justify="center">
                <Typography>{t('importExportExcel:description.message')}</Typography>
                <Typography>{t('importExportExcel:description.note')}</Typography>
            </Row>
        );
    }, [errors, updatedCount, t]);

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="close" onClick={handleOnClose}>
                {t('importExportExcel:actions.close')}
            </CancelButton>
        );

        if (!isNil(errors) || !isNil(updatedCount)) {
            return [cancelButton];
        }

        const okButton = (
            <StyledUpload
                key="upload"
                accept={accept}
                beforeUpload={beforeUpload}
                onChange={handleOnChange}
                showUploadList={false}
                style={{ marginLeft: '1rem' }}
            >
                <OkButton>{t('importExportExcel:actions.uploadFile')}</OkButton>
            </StyledUpload>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [accept, beforeUpload, errors, handleOnChange, handleOnClose, t, theme, updatedCount]);

    return (
        <Modal footer={footer} onCancel={handleOnClose} open={open} title={t('inventoryList:modal.title')} width={550}>
            {content}
        </Modal>
    );
};

export default ImportInventoryModal;

export const useImportInventoryModal = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => <ImportInventoryModal onClose={actions.close} open={visible} />,
    };
};
